import { useCallback, useMemo, useState } from "react";
import { Entity } from "@common/types/entity";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import {
    useCreateLedger,
    useLedgers,
    useSetLedgerStatus,
} from "@api/ledger.api";
import {
    AccountingLedgerBasis,
    AccountingLedgerStatus,
} from "@common/types/domains/accounting/ledger";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { FeatureAccess } from "@components/settings/FeatureAccess/FeatureAccess";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";

interface Props {
    entityId: Entity["id"];
}

export const CreateAccrualLedgerButton: React.FC<Props> = ({ entityId }) => {
    const { data: ledgers, isLoading: ledgerIsLoading } = useLedgers(
        entityId,
        false,
        { includeHasJournalEntries: true },
    );
    const accrualLedger = useMemo(
        () =>
            ledgers?.find(
                (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
            ),
        [ledgers],
    );
    const hasAccrualLedger = !!accrualLedger;
    const createLedgerMutation = useCreateLedger();
    const setLedgerStatus = useSetLedgerStatus();
    const { error: errorToast } = useToaster();
    const [isHideConfirmOpen, setIsHideConfirmOpen] = useState(false);

    const hideAccrualLedger = useCallback(async () => {
        if (!accrualLedger) {
            return;
        }
        try {
            await setLedgerStatus.mutateAsync({
                params: { entityId, ledgerId: accrualLedger.id },
                body: { status: AccountingLedgerStatus.archived },
            });
            setIsHideConfirmOpen(false);
        } catch {
            errorToast("Failed to hide accrual ledger");
        }
    }, [accrualLedger, entityId, errorToast, setLedgerStatus]);

    const handleClick = useCallback(async () => {
        if (accrualLedger) {
            if (accrualLedger.hasJournalEntries) {
                setIsHideConfirmOpen(true);
                return;
            }
            await hideAccrualLedger();
            return;
        }
        await createLedgerMutation.mutateAsync({
            body: { basis: AccountingLedgerBasis.accruals },
            params: { entityId },
        });
    }, [accrualLedger, createLedgerMutation, entityId, hideAccrualLedger]);

    return (
        <FeatureAccess
            feature={PREMIUM_FEATURES.ACCRUAL_LEDGER}
            renderAllowed={() => (
                <>
                    <ButtonWithLoader
                        variant={hasAccrualLedger ? "secondary" : "primary"}
                        size="md"
                        loading={
                            createLedgerMutation.isPending ||
                            setLedgerStatus.isPending ||
                            ledgerIsLoading
                        }
                        onClick={handleClick}
                    >
                        {hasAccrualLedger
                            ? "Hide Accrual ledger"
                            : "Create Accrual ledger"}
                    </ButtonWithLoader>
                    <ConfirmationModal
                        show={isHideConfirmOpen}
                        title="Hide Accrual ledger?"
                        question="This ledger has journal entries. Hiding it keeps those entries but turns off accrual features for this entity. You can create the ledger again later."
                        yes="Hide"
                        yesBtnVariant="destructive"
                        no="Cancel"
                        onReject={() => setIsHideConfirmOpen(false)}
                        onHide={() => setIsHideConfirmOpen(false)}
                        onConfirm={hideAccrualLedger}
                    />
                </>
            )}
            renderBlocked={({ openPlans, canManageBilling }) => (
                <Tooltip content="Upgrade your plan to use accrual accounting">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={canManageBilling ? openPlans : undefined}
                        disabled={!canManageBilling}
                        data-testid="create-accrual-ledger-btn-upgrade"
                    >
                        Create Accrual ledger
                    </Button>
                </Tooltip>
            )}
        />
    );
};
